import { dateInputValue, formatDuration } from '../../../lib/format.js'
/**
 * The geometry behind the dispatch board.
 *
 * Everything here is pure, because getting it wrong produces appointments at
 * 09:07 that nobody asked for, and that is much easier to catch in a test than
 * by simulating a drag.
 *
 * Two rules the whole board depends on:
 *
 * - Time is quantised to 15 minutes. A drop that lands at 09:07 is noise; a
 *   dispatcher means nine o'clock.
 * - Duration is preserved when a block moves. Dragging to a new time must not
 *   silently resize the job. Resizing is a separate gesture on the edges.
 *
 * Pixels per slot is an argument rather than a constant, because both boards
 * size a quarter hour to the room they have. The default is the floor, and it
 * is there for the tests below — anything drawing on screen passes the size it
 * measured, or it will draw at one scale and snap at another.
 */

export const SLOT_MINUTES = 15

/**
 * The drawing surface: the hours a booking may be *drawn* in.
 *
 * Not the working day — see `Workday` below, which is the account's setting and
 * the thing capacity is measured against. This pair is the canvas, and it is
 * deliberately much wider than anyone works: an early start, a late callout and
 * a job that ran over all have to be visible, and a block drawn outside the
 * canvas is a block a dispatcher cannot see or drag.
 *
 * Fixed rather than derived from the setting, because every pixel constant
 * below divides this span up. Making the canvas configurable would turn each of
 * them into a function for no gain — fifteen hours contains any workday this
 * business will ever have, and the part outside working hours is shaded rather
 * than absent.
 */
export const WORK_START_HOUR = 6
export const WORK_END_HOUR = 19

/** Quiet buffer either side of the canvas — visible, shaded darker. */
export const AFTER_HOURS_BUFFER_HOURS = 2

export const DAY_START_HOUR = WORK_START_HOUR - AFTER_HOURS_BUFFER_HOURS
export const DAY_END_HOUR = WORK_END_HOUR + AFTER_HOURS_BUFFER_HOURS
export const MINUTES_VISIBLE = (DAY_END_HOUR - DAY_START_HOUR) * 60

/** Quarter-hours in the visible day, which is what both boards divide up. */
export const VISIBLE_SLOTS = MINUTES_VISIBLE / SLOT_MINUTES

/** Smallest a quarter hour may be drawn before a board starts scrolling. */
export const DAY_MIN_SLOT_HEIGHT = 14
export const TIMELINE_MIN_SLOT_WIDTH = 13

/**
 * How far the timeline is zoomed past fitting the day to the window.
 *
 * `1` is the fit-to-width scale; higher factors widen every booking so short
 * visits stay readable, and the board scrolls sideways instead.
 */
export const TIMELINE_ZOOM_LEVELS = [1, 1.5, 2, 3] as const
export type TimelineZoom = (typeof TIMELINE_ZOOM_LEVELS)[number]

/** Furniture at the head of each board, which is not part of the day. */
/**
 * The band above a section's columns on the day board.
 *
 * Short on purpose: it is a label and a collapse target, and every pixel it
 * takes is a pixel off the part of the screen that shows work.
 */
export const SECTION_HEADER_HEIGHT = 22

export const DAY_HEADER_HEIGHT = 56
/** Wide enough for avatar, name, load bar, free/drive labels and a zig-zag chip. */
export const TIMELINE_HEADER_WIDTH = 252

/**
 * How big a quarter hour is when the day is spread across the space available.
 *
 * A board is a fixed span (working day plus after-hours buffers) however large
 * the window is, so a fixed slot leaves most of a desktop screen empty and
 * squeezes a laptop. Below the floor the board scrolls instead of drawing
 * slots too small to hit. The spare pixel keeps a sub-pixel rounding error
 * from producing a scrollbar for a fraction of a slot.
 *
 * `zoom` multiplies that fit width so a dispatcher can enlarge bookings without
 * changing the day window. Only the timeline uses it; the day board still fills
 * the height it has.
 */
export function daySlotHeight(availableHeight: number): number {
  return spreadDay(availableHeight - DAY_HEADER_HEIGHT, DAY_MIN_SLOT_HEIGHT)
}

export function timelineSlotWidth(availableWidth: number, zoom: TimelineZoom = 1): number {
  return spreadDay(availableWidth - TIMELINE_HEADER_WIDTH, TIMELINE_MIN_SLOT_WIDTH) * zoom
}

/** Coerce a URL/query value onto a known zoom step; unknown values fall to fit. */
export function parseTimelineZoom(value: string | number): TimelineZoom {
  const n = typeof value === 'string' ? Number(value) : value
  return (TIMELINE_ZOOM_LEVELS as readonly number[]).includes(n) ? (n as TimelineZoom) : 1
}

/** Next zoom step, or the current value when already at the end. */
export function stepTimelineZoom(current: TimelineZoom, direction: -1 | 1): TimelineZoom {
  const index = TIMELINE_ZOOM_LEVELS.indexOf(current)
  const next = TIMELINE_ZOOM_LEVELS[index + direction]
  return next ?? current
}

function spreadDay(usable: number, minimum: number): number {
  return Math.max(minimum, (usable - 1) / VISIBLE_SLOTS)
}

/**
 * The account's working day, in minutes from midnight.
 *
 * `Setting.workdayStartMinutes` / `workdayEndMinutes`, which the board ignored
 * until now: capacity was a hardcoded nine hours, so editing the hours in
 * Settings changed nothing a dispatcher could see. Everything that asks "how
 * long is a day" — the load bars, the free-time labels, the slot the unbooked
 * rail proposes, and the router — reads this one value, or they disagree.
 */
export interface Workday {
  startMinutes: number
  endMinutes: number
}

/** Eight to five, matching the seed. Used until the account's row loads. */
export const DEFAULT_WORKDAY: Workday = { startMinutes: 8 * 60, endMinutes: 17 * 60 }

/**
 * A workday clamped into the canvas and guaranteed to be a positive span.
 *
 * A settings row with the end before the start, or hours outside the drawn
 * day, would otherwise produce negative capacity and blocks with nowhere to be.
 */
export function clampWorkday(workday: Workday): Workday {
  const floor = DAY_START_HOUR * 60
  const ceiling = DAY_END_HOUR * 60

  const startMinutes = Math.min(Math.max(workday.startMinutes, floor), ceiling - SLOT_MINUTES)
  const endMinutes = Math.min(Math.max(workday.endMinutes, startMinutes + SLOT_MINUTES), ceiling)

  return { startMinutes, endMinutes }
}

/** How long the working day is, which is what capacity is a percentage of. */
export function workdayMinutes(workday: Workday): number {
  const { startMinutes, endMinutes } = clampWorkday(workday)
  return endMinutes - startMinutes
}

/**
 * Quarter-hours of canvas outside working hours, at each end.
 *
 * The shaded bands. Derived from the account's hours rather than from a
 * constant, so a business that starts at six sees six o'clock unshaded.
 */
export function shadedSlots(workday: Workday): { leading: number; trailing: number } {
  const { startMinutes, endMinutes } = clampWorkday(workday)
  const windowStart = DAY_START_HOUR * 60
  const windowEnd = DAY_END_HOUR * 60

  return {
    leading: Math.round((startMinutes - windowStart) / SLOT_MINUTES),
    trailing: Math.round((windowEnd - endMinutes) / SLOT_MINUTES),
  }
}

/** Pixel offset within a column to minutes past the start of the window. */
export function snapMinutes(offsetPx: number, slotPx = DAY_MIN_SLOT_HEIGHT): number {
  const rawMinutes = (Math.max(0, offsetPx) / slotPx) * SLOT_MINUTES
  const snapped = Math.round(rawMinutes / SLOT_MINUTES) * SLOT_MINUTES
  return Math.min(snapped, MINUTES_VISIBLE - SLOT_MINUTES)
}

/** Pixel offset within a column to a quarter-hour boundary. */
export function snapToSlot(windowStart: Date, offsetPx: number, slotPx = DAY_MIN_SLOT_HEIGHT): Date {
  return addMinutes(windowStart, snapMinutes(offsetPx, slotPx))
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000)
}

export function minutesBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 60_000)
}

export function minutesToPx(minutes: number, slotPx = DAY_MIN_SLOT_HEIGHT): number {
  return (minutes / SLOT_MINUTES) * slotPx
}

/**
 * Where a block sits and how tall it is.
 *
 * A block is drawn the length it is booked for, at every zoom level, so what is
 * on screen can be read off against the grid. It used to carry a two-slot floor
 * to keep a quarter-hour call legible, which made every fifteen-minute booking
 * paint half an hour wide and cover the start of whatever came next — the block
 * behind it kept lane 0, because `packLanes` works on the real times and
 * back-to-back work does not overlap. Legibility for short bookings is what
 * timeline zoom is for.
 *
 * Both edges land on a quarter-hour boundary, the same grid a drag snaps to, so
 * the handful of migrated visits booked at 09:07 line up with everything else.
 * Clamped to the visible window as well, so a visit that starts before the
 * board opens appears at the leading edge rather than off-screen.
 */
export function blockGeometry(
  windowStart: Date,
  startAt: Date,
  endAt: Date,
  slotPx = DAY_MIN_SLOT_HEIGHT,
): { offset: number; size: number; durationMinutes: number } {
  const durationMinutes = Math.max(SLOT_MINUTES, minutesBetween(startAt, endAt))
  const startMinutes = minutesBetween(windowStart, startAt)

  const visibleStart = toSlotBoundary(Math.max(0, startMinutes))
  const visibleEnd = toSlotBoundary(Math.min(MINUTES_VISIBLE, startMinutes + durationMinutes))

  return {
    offset: minutesToPx(visibleStart, slotPx),
    size: minutesToPx(Math.max(SLOT_MINUTES, visibleEnd - visibleStart), slotPx),
    durationMinutes,
  }
}

/**
 * Nearest quarter hour. Rounding is monotonic, so two consecutive bookings stay
 * consecutive rather than being rounded into an overlap.
 */
function toSlotBoundary(minutes: number): number {
  return Math.round(minutes / SLOT_MINUTES) * SLOT_MINUTES
}

/**
 * Moves a block to a new start, keeping its length.
 *
 * The pointer grabbed the block somewhere in its middle, and that offset has
 * to survive the drag — otherwise grabbing a two-hour job by its centre snaps
 * it an hour earlier the moment you let go.
 */
export function moveTo(
  windowStart: Date,
  pointerOffsetPx: number,
  grabOffsetPx: number,
  durationMinutes: number,
  slotPx = DAY_MIN_SLOT_HEIGHT,
): { startAt: Date; endAt: Date } {
  const topPx = pointerOffsetPx - grabOffsetPx
  // Clamped so a long job dragged to the bottom of the grid ends at the close
  // of the window rather than hanging off it.
  const latestStart = Math.max(0, MINUTES_VISIBLE - durationMinutes)
  const startMinutes = Math.min(snapMinutes(topPx, slotPx), latestStart)
  const startAt = addMinutes(windowStart, startMinutes)

  return { startAt, endAt: addMinutes(startAt, durationMinutes) }
}

/**
 * Drags one edge of a block.
 *
 * The opposite edge is fixed, and a visit can never be shorter than one slot:
 * dragging the top past the bottom pins it at fifteen minutes rather than
 * inverting the booking.
 */
export function resizeTo(
  windowStart: Date,
  edge: 'start' | 'end',
  pointerOffsetPx: number,
  fixed: Date,
  slotPx = DAY_MIN_SLOT_HEIGHT,
): { startAt: Date; endAt: Date } {
  const pointerMinutes = snapMinutes(pointerOffsetPx, slotPx)
  const fixedMinutes = minutesBetween(windowStart, fixed)

  if (edge === 'start') {
    const startMinutes = Math.min(pointerMinutes, fixedMinutes - SLOT_MINUTES)
    return { startAt: addMinutes(windowStart, startMinutes), endAt: fixed }
  }

  const endMinutes = Math.max(pointerMinutes, fixedMinutes + SLOT_MINUTES)
  return { startAt: fixed, endAt: addMinutes(windowStart, endMinutes) }
}

/**
 * Draws a new slot from where the pointer went down to where it is now.
 *
 * Both edges snap to the quarter-hour grid. Dragging back past the origin
 * swaps them rather than inverting the booking. A click that has not left
 * its slot is one quarter hour — the same floor as a resize. The later edge
 * may land on the close of the window; `snapMinutes` is a start time and
 * would have cut the last slot off.
 */
export function spanTo(
  windowStart: Date,
  originOffsetPx: number,
  pointerOffsetPx: number,
  slotPx = DAY_MIN_SLOT_HEIGHT,
): { startAt: Date; endAt: Date } {
  const origin = Math.min(
    toSlotBoundary((Math.max(0, originOffsetPx) / slotPx) * SLOT_MINUTES),
    MINUTES_VISIBLE,
  )
  const pointer = Math.min(
    toSlotBoundary((Math.max(0, pointerOffsetPx) / slotPx) * SLOT_MINUTES),
    MINUTES_VISIBLE,
  )
  const startMinutes = Math.max(0, Math.min(origin, pointer, MINUTES_VISIBLE - SLOT_MINUTES))
  const later = Math.max(origin, pointer)
  const endMinutes = Math.max(startMinutes + SLOT_MINUTES, Math.min(later, MINUTES_VISIBLE))

  return { startAt: addMinutes(windowStart, startMinutes), endAt: addMinutes(windowStart, endMinutes) }
}

export interface Span {
  startAt: Date
  endAt: Date
}

export function overlaps(a: Span, b: Span): boolean {
  return a.startAt < b.endAt && b.startAt < a.endAt
}

/**
 * Side-by-side lanes for visits booked at the same time.
 *
 * Two jobs on one person at ten o'clock is sometimes correct — a quick call
 * squeezed into a longer treatment — and drawing them on top of each other
 * hides one of them completely. Overlapping blocks share the column width
 * instead.
 *
 * Clusters are found by running maximum end: everything that transitively
 * overlaps shares a lane count, so a column of three staggered visits reads as
 * three even widths rather than two of one width and one of another.
 */
export function packLanes<T extends Span>(items: T[]): Array<{ item: T; lane: number; lanes: number }> {
  const sorted = [...items].sort(
    (a, b) => a.startAt.getTime() - b.startAt.getTime() || b.endAt.getTime() - a.endAt.getTime(),
  )

  const placed: Array<{ item: T; lane: number; lanes: number }> = []
  let cluster: Array<{ item: T; lane: number; lanes: number }> = []
  let clusterEnd = -Infinity

  const closeCluster = () => {
    const lanes = cluster.reduce((widest, entry) => Math.max(widest, entry.lane + 1), 1)
    for (const entry of cluster) entry.lanes = lanes
    placed.push(...cluster)
    cluster = []
    clusterEnd = -Infinity
  }

  for (const item of sorted) {
    if (item.startAt.getTime() >= clusterEnd) closeCluster()

    // The lowest lane whose current occupant has already finished.
    const busy = new Set(
      cluster.filter((entry) => entry.item.endAt > item.startAt).map((entry) => entry.lane),
    )
    let lane = 0
    while (busy.has(lane)) lane += 1

    cluster.push({ item, lane, lanes: 1 })
    clusterEnd = Math.max(clusterEnd, item.endAt.getTime())
  }

  closeCluster()
  return placed
}

/** Booked minutes for a set of visits, ignoring anything cancelled. */
export function bookedMinutes(visits: Array<Span & { status?: string }>): number {
  return visits
    .filter((visit) => visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW')
    .reduce((total, visit) => total + minutesBetween(visit.startAt, visit.endAt), 0)
}

/**
 * Minutes of a set of spans that fall inside a window, each clipped to it.
 *
 * Used to count leave against the working day: a week of annual leave should
 * only count the one day currently on screen, not the whole span, and a
 * half-day of it starting before the window opens should not count the part
 * that already happened.
 */
export function clippedMinutes(spans: Span[], windowStart: Date, windowEnd: Date): number {
  return spans.reduce((total, span) => {
    const start = span.startAt > windowStart ? span.startAt : windowStart
    const end = span.endAt < windowEnd ? span.endAt : windowEnd
    return total + Math.max(0, minutesBetween(start, end))
  }, 0)
}

/**
 * Capacity shading for the week grid.
 *
 * Six buckets against the working day, greening up as the day fills and
 * turning Charcoal past capacity. Charcoal rather than red because an
 * over-full day is a thing to look at, not necessarily a mistake.
 */
export type HeatLevel = 'empty' | 'quiet' | 'light' | 'busy' | 'full' | 'over'

export function heatLevel(percentBooked: number): HeatLevel {
  if (percentBooked <= 1) return 'empty'
  if (percentBooked < 35) return 'quiet'
  if (percentBooked < 60) return 'light'
  if (percentBooked < 85) return 'busy'
  if (percentBooked <= 102) return 'full'
  return 'over'
}

export const HEAT_BACKGROUND: Record<HeatLevel, string> = {
  empty: '#ffffff',
  quiet: '#f7f9f3',
  light: '#eef6e0',
  busy: '#dbeec4',
  full: '#a8d666',
  over: '#1e2320',
}

export interface Coordinates {
  latitude: number
  longitude: number
}

const EARTH_RADIUS_KM = 6371

/**
 * Great-circle distance between two points, in kilometres.
 *
 * Haversine, not a routing call — a straight line, always labelled as such
 * wherever it is shown, because it is not a drive time.
 */
export function kilometresBetween(from: Coordinates, to: Coordinates): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180

  const dLat = toRadians(to.latitude - from.latitude)
  const dLon = toRadians(to.longitude - from.longitude)

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(dLon / 2) ** 2

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a))
}

export type Proximity = 'near' | 'mid' | 'far'

/** Under this and a job is practically next door. */
const NEAR_KM = 5
/** Past this and it is worth a second thought before booking it in. */
const FAR_KM = 20

/** Buckets a straight-line distance for the board's drag-time highlight. */
export function proximityOf(km: number): Proximity {
  if (km <= NEAR_KM) return 'near'
  if (km >= FAR_KM) return 'far'
  return 'mid'
}

/** "850m" below a kilometre, "3.2km" above it — metres read as false precision past that. */
export function formatKm(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`
}

/** The closest existing booking to whatever is being dragged, or null with nothing to compare. */
export function nearestKm(distanceKm: ReadonlyMap<string, number>): number | null {
  return distanceKm.size === 0 ? null : Math.min(...distanceKm.values())
}

/** "1h 30m", "45m", "0m". Never "1h 0m". */
/** "1h 30m" — `formatDuration`, under the board's name. */
export function durationLabel(minutes: number): string {
  return formatDuration(minutes)
}

/**
 * Gaps in someone's day long enough to be worth filling.
 *
 * Anything under the threshold is travel and paperwork, not an opening.
 */
export function gapsIn(
  visits: Span[],
  windowStart: Date,
  windowEnd: Date,
  minimumMinutes = 60,
): Span[] {
  const sorted = [...visits].sort((a, b) => a.startAt.getTime() - b.startAt.getTime())
  const gaps: Span[] = []
  let cursor = windowStart

  for (const visit of sorted) {
    if (visit.startAt > cursor && minutesBetween(cursor, visit.startAt) >= minimumMinutes) {
      gaps.push({ startAt: cursor, endAt: visit.startAt })
    }
    if (visit.endAt > cursor) cursor = visit.endAt
  }

  if (cursor < windowEnd && minutesBetween(cursor, windowEnd) >= minimumMinutes) {
    gaps.push({ startAt: cursor, endAt: windowEnd })
  }

  return gaps
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export function startOfDay(date: Date): Date {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

/** Monday. The trade week starts when the phones do. */
export function startOfWeek(date: Date): Date {
  const next = startOfDay(date)
  const shift = (next.getDay() + 6) % 7
  next.setDate(next.getDate() - shift)
  return next
}

/** The day as `yyyy-mm-dd` from its local parts — `dateInputValue`, under the board's name. */
export function isoDate(date: Date): string {
  return dateInputValue(date)
}

export function isSameDay(a: Date, b: Date): boolean {
  return isoDate(a) === isoDate(b)
}

/**
 * The window shown for a given day — the canvas, not the working day.
 *
 * Deliberately not a function of `Workday`. Capacity, the proposed slot and the
 * router all read the account's hours; this is the span the grid draws, and it
 * stays fixed so the pixel arithmetic above stays constant. `shadedSlots` is
 * how the configured hours become visible inside it.
 */
export function dayWindow(day: Date): { windowStart: Date; windowEnd: Date } {
  const base = startOfDay(day)
  return {
    windowStart: addMinutes(base, DAY_START_HOUR * 60),
    windowEnd: addMinutes(base, DAY_END_HOUR * 60),
  }
}
